/**
 * Wishlist Drawer - Custom implementation using Wishlist Hero JS API
 *
 * This module provides a side drawer for wishlist functionality similar to the cart drawer.
 * It integrates with Wishlist Hero's JS SDK for data management.
 *
 * Key features:
 * - Displays wishlist items in a side drawer
 * - Variant selector for items without specific variant (marked with trailing whitespaces)
 * - Remove from wishlist functionality
 * - Move to cart functionality
 * - Real-time updates when wishlist changes
 */

import { showToast } from '@/partials/el.Utils.js';
import {
  trackAddedToWishlist,
  trackRemovedFromWishlist,
  trackClearedWishlist,
} from '@/partials/el.WishlistTracking.js';

const SELECTORS = {
  DRAWER_INNER: '#WishlistDrawer-Inner',
  REMOVE_BUTTON: '.js-wishlist-remove',
  MOVE_TO_CART_BUTTON: '.js-wishlist-move-to-cart',
  CLEAR_ALL_BUTTON: '.js-wishlist-clear-all',
  SIZE_SELECTOR: '.el-wishlist-drawer__item-size-selector',
  SIZE_OPTION_BUTTON: '.el-wishlist-drawer__item-size-selector--tooltip-option',
  ITEM: '.el-wishlist-drawer__item',
  ITEMS_CONTAINER: '.el-wishlist-drawer__items',
  EMPTY_STATE: '.el-wishlist-drawer__empty',
  WISHLIST_TRIGGER: '.js-wishlist-trigger',
  WISHLIST_TRIGGER_IN_DRAWER: '.js-wishlist-trigger--in-drawer',
};

const NO_VARIANT_MARKER = '   '; // 3 whitespaces at the end of product name

elabs.WishlistDrawer = class {
  constructor() {
    if (elabs.WishlistDrawer.instance) {
      return elabs.WishlistDrawer.instance;
    }
    elabs.WishlistDrawer.instance = this;

    this.drawer = null;
    this.wishlistItems = [];
    this.productDataCache = new Map();
    this.isInitialized = false;

    this._waitForSDK();
  }

  /**
   * Wait for Wishlist Hero SDK to be ready
   */
  _waitForSDK() {
    if (window.WishListHero_SDK) {
      this._initialize();
    } else {
      document.addEventListener('wishlist-hero-wishlist-sdk-ready', () => {
        this._initialize();
      });
    }
  }

  /**
   * Initialize the wishlist drawer
   */
  _initialize() {
    this.isInitialized = true;
    this._subscribeToWishlistUpdates();
    this._initWishlistTriggers();
    this._updateWishlistTriggerVisibility();
    this._initialiseShowWishlist();
  }

  /**
   * Handle ?show-wishlist=true URL parameter
   * Opens the wishlist drawer and removes the parameter from the URL
   */
  _initialiseShowWishlist = () => {
    if (window.location.search.includes('show-wishlist=true')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('show-wishlist');
      history.replaceState(null, null, url.toString());
      setTimeout(() => {
        this.openDrawer();
      }, 500);
    }
  };

  /**
   * Initialize wishlist trigger buttons (header and nav drawer)
   */
  _initWishlistTriggers() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(SELECTORS.WISHLIST_TRIGGER);
      if (!trigger) {
        return;
      }

      e.preventDefault();

      // Check if trigger is inside a drawer (nav menu)
      const isInDrawer = trigger.classList.contains('js-wishlist-trigger--in-drawer');
      this.openDrawer(isInDrawer);
    });
  }

  /**
   * Update visibility of wishlist trigger buttons based on wishlist count
   */
  _updateWishlistTriggerVisibility = async () => {
    const count = await this.getWishlistCount();
    const displayValue = count > 0 ? '' : 'none';

    // Update triggers in main document
    const triggers = document.querySelectorAll(SELECTORS.WISHLIST_TRIGGER);
    triggers.forEach((trigger) => {
      trigger.style.display = displayValue;
    });

    // Also update triggers inside templates (for nav drawer)
    const templates = document.querySelectorAll('template');
    templates.forEach((template) => {
      const templateTriggers = template.content.querySelectorAll(SELECTORS.WISHLIST_TRIGGER);
      templateTriggers.forEach((trigger) => {
        trigger.style.display = displayValue;
      });
    });
  };

  /**
   * Subscribe to wishlist update events
   */
  _subscribeToWishlistUpdates() {
    // Listen for custom wishlist update events
    document.addEventListener('wishlist:updated', () => {
      this._refreshDrawerContent();
      this._updateWishlistTriggerVisibility();
    });
  }

  /**
   * Open the wishlist drawer
   * @param {boolean} closeCurrentOverlay - Whether to close current overlay first with delay
   */
  openDrawer = async (closeCurrentOverlay = false) => {
    // If another overlay is open, close it first and wait
    if (closeCurrentOverlay && document.body.classList.contains('el-overlay-menu-open')) {
      elabs.overlayMenuService.closeMenu();
      await new Promise((resolve) => setTimeout(resolve, 350));
    }

    await this._loadWishlistItems();
    const htmlContent = this._generateDrawerHTML();

    elabs.overlayMenuService.showMenu({ htmlContent }, 'right', { wrapperClass: 'el-overlay-menu--wishlist' });

    // Wait for DOM to be ready, then add event listeners and update prices
    setTimeout(() => {
      this._addEventListeners();
      // Fetch real prices from Shopify API and update display
      this._updatePricesFromShopify();
    }, 50);
  };

  /**
   * Load wishlist items from Wishlist Hero SDK
   */
  _loadWishlistItems = async () => {
    if (!window.WishListHero_SDK) {
      this.wishlistItems = [];
      return;
    }

    try {
      this.wishlistItems = window.WishListHero_SDK.GetWishListItems() || [];
    } catch (error) {
      console.error('Error loading wishlist items:', error);
      this.wishlistItems = [];
    }
  };

  /**
   * Check if item has no specific variant selected (marked with trailing whitespaces)
   */
  _hasNoVariantSelected(item) {
    return item.ProductName && item.ProductName.endsWith(NO_VARIANT_MARKER);
  }

  /**
   * Get clean product name without variant marker
   */
  _getCleanProductName(item) {
    if (this._hasNoVariantSelected(item)) {
      return item.ProductName.slice(0, -NO_VARIANT_MARKER.length);
    }
    return item.ProductName;
  }

  /**
   * Generate the drawer HTML content
   */
  _generateDrawerHTML() {
    const itemsHTML = this.wishlistItems.length > 0 ? this._generateItemsHTML() : '';

    const emptyClass = this.wishlistItems.length === 0 ? '' : 'hidden';
    const contentClass = this.wishlistItems.length > 0 ? '' : 'hidden';

    return `
      <div class="el-wishlist-drawer__inner" id="WishlistDrawer-Inner">
        <div class="el-wishlist-drawer__content">
          <div class="el-wishlist-drawer__empty ${emptyClass}">
            <h4 class="el-wishlist-drawer__empty-title">Your wishlist is empty</h4>
            <a href="${this._getRoute('allProducts')}" class="el-wishlist-drawer__empty-button">Continue shopping</a>
          </div>
          
          <div class="el-wishlist-drawer__with-items ${contentClass}">
            <div class="el-wishlist-drawer__header">
              <h3 class="el-wishlist-drawer__title">Wishlist</h3>
              <button type="button" class="el-wishlist-drawer__clear-btn js-wishlist-clear-all">Clear All</button>
            </div>
            
            <div class="el-wishlist-drawer__body-wrapper">
              <div class="el-wishlist-drawer__body">
                <div class="el-wishlist-drawer__items">
                  ${itemsHTML}
                </div>
              </div>
            </div>
            
            <div class="el-wishlist-drawer__footer">
              <a href="${this._getRoute('allProducts')}" class="el-wishlist-drawer__continue-button">
                Continue Shopping
              </a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate HTML for all wishlist items
   */
  _generateItemsHTML() {
    return this.wishlistItems.map((item) => this._generateItemHTML(item)).join('');
  }

  /**
   * Generate HTML for a single wishlist item
   */
  _generateItemHTML(item) {
    const hasNoVariant = this._hasNoVariantSelected(item);
    const cleanName = this._getCleanProductName(item);
    const imageUrl = item.ProductImage?.startsWith('//') ? `https:${item.ProductImage}` : item.ProductImage;
    const productUrl = item.ProductLink || '#';
    // Get price - SDK returns Price after reload, ProductPrice immediately after add
    const itemPrice = item.Price || item.ProductPrice;
    // Show loading indicator if no price, will be updated from Shopify API
    const price = itemPrice ? this._formatPrice(itemPrice) : '...';

    // Size selector or size display
    const sizeHTML = hasNoVariant ? this._generateSizeSelectorHTML(item) : this._generateSizeDisplayHTML(item);

    // Image HTML
    const imageHTML = imageUrl
      ? `<img src="${imageUrl}" class="el-wishlist-drawer__item-image" alt="${cleanName}" loading="lazy" width="150">`
      : '<div class="el-wishlist-drawer__item-placeholder"></div>';

    return `
      <div class="el-wishlist-drawer__item" 
           data-variant-id="${item.ProductVariantId}" 
           data-product-id="${item.ProductId}"
           data-has-no-variant="${hasNoVariant}">
        <a href="${productUrl}" class="el-wishlist-drawer__item-image-wrapper" aria-label="${cleanName}">
          ${imageHTML}
        </a>
        
        <div class="el-wishlist-drawer__item-content">
          <div class="el-wishlist-drawer__item-title-wrapper">
            <a href="${productUrl}" class="el-wishlist-drawer__item-title">${cleanName}</a>
            <div class="el-wishlist-drawer__loader-spinner-wrapper hidden">
              <span class="loader-spinner" role="status" aria-label="Updating"></span>
            </div>
          </div>
          
          ${sizeHTML}
          
          <div class="el-wishlist-drawer__item-price">
            <span class="el-price el-price--inline">
              <span class="el-price__inner">
                <span class="el-price__compare-at-price"></span>
                <span class="el-price__price">${price}</span>
              </span>
            </span>
          </div>
          
          <div class="el-wishlist-drawer__item-actions">
            <button class="el-wishlist-drawer__item-move-to-cart js-wishlist-move-to-cart" 
                    aria-label="Move to cart"
                    ${hasNoVariant ? 'data-needs-variant="true"' : ''}>
              <img class="el-wishlist-drawer__item-move-to-cart--icon" 
                   src="${window.wishlistDrawerConfig?.icons?.cart || ''}" 
                   width="10" height="10" loading="lazy" alt="" aria-hidden="true">
              <span class="el-wishlist-drawer__item-move-to-cart--text">Move to Cart</span>
            </button>
            
            <button class="el-wishlist-drawer__item-remove js-wishlist-remove" aria-label="Remove item">
              <img class="el-wishlist-drawer__item-remove--icon" 
                   src="${window.wishlistDrawerConfig?.icons?.close || ''}" 
                   width="6" height="6" loading="lazy" alt="" aria-hidden="true">
              <span class="el-wishlist-drawer__item-remove--text">Remove</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate size display HTML for items with selected variant
   * Size will be populated asynchronously from Shopify API
   */
  _generateSizeDisplayHTML() {
    // Empty container - will be populated by _updatePricesFromShopify if product has variants
    return `
      <div class="el-wishlist-drawer__item-size-wrapper"></div>
    `;
  }

  /**
   * Generate size selector HTML for items without specific variant
   */
  _generateSizeSelectorHTML(item) {
    const productUrl = item.ProductLink || '';
    // Use single quotes for inner HTML to avoid breaking the attribute
    const loadingContent =
      "<div class='el-wishlist-drawer__item-size-selector--tooltip'>" +
      "<div class='el-wishlist-drawer__size-loading'>Loading sizes...</div></div>";
    const arrowSvg =
      '<svg class="el-wishlist-drawer__item-size-selector--arrow" width="7" height="7" ' +
      'viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M3.49998 5.49508C3.37453 5.49508 3.24909 5.44718 3.15344 5.35158L0.143599 ' +
      '2.3417C-0.0478663 2.15024 -0.0478663 1.83981 0.143599 1.64842C0.334987 1.45704 0.645353 ' +
      '1.45704 0.836834 1.64842L3.49998 4.31173L6.16314 1.64852C6.35461 1.45713 6.66494 1.45713 ' +
      '6.85632 1.64852C7.04787 1.8399 7.04787 2.15033 6.85632 2.3418L3.84652 5.35167C3.75083 ' +
      '5.44729 3.62539 5.49508 3.49998 5.49508Z" fill="currentColor"/></svg>';

    return `
      <div class="el-wishlist-drawer__item-size-wrapper">
        <div class="el-wishlist-drawer__item-size-selector"
             role="button"
             tabindex="0"
             aria-haspopup="true"
             aria-label="Select size"
             data-product-id="${item.ProductId}"
             data-variant-id="${item.ProductVariantId}"
             data-product-url="${productUrl}"
             data-tippy-content="${loadingContent}"
             data-tippy-allowhtml="true"
             data-tippy-interactive="true"
             data-tippy-placement-mobile="bottom-start"
             data-tippy-placement-desktop="bottom-start"
             data-tippy-append-to="parent"
             data-tippy-arrow="false"
             data-tippy-trigger="click"
             data-tippy-offset="[0]"
             data-tippy-max-width="500">
          <span class="el-wishlist-drawer__item-size-selector--label">Select Size</span>
          ${arrowSvg}
        </div>
      </div>
    `;
  }

  /**
   * Format price for display
   */
  _formatPrice(price) {
    if (!price && price !== 0) {
      return '';
    }

    // Get currency symbol from Shopify
    const currencySymbol = window.Shopify?.currency?.active
      ? this._getCurrencySymbol(window.Shopify.currency.active)
      : '£';

    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) {
      return '';
    }

    // Format without trailing zeros
    const formatted = numPrice % 1 === 0 ? numPrice.toFixed(0) : numPrice.toFixed(2).replace(/\.?0+$/, '');

    return `${currencySymbol}${formatted}`;
  }

  /**
   * Get currency symbol from ISO code
   */
  _getCurrencySymbol(isoCode) {
    const symbols = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      CAD: 'CA$',
      AUD: 'A$',
    };
    return symbols[isoCode] || isoCode;
  }

  /**
   * Get route from config
   */
  _getRoute(routeName) {
    const config = window.wishlistDrawerConfig;
    if (config?.routes?.[routeName]) {
      return config.routes[routeName];
    }
    // Fallbacks
    const fallbacks = {
      allProducts: '/collections/all',
      root: '/',
    };
    return fallbacks[routeName] || '/';
  }

  /**
   * Add event listeners to the drawer
   */
  _addEventListeners = () => {
    this.drawer = document.querySelector(SELECTORS.DRAWER_INNER);
    if (!this.drawer) {
      return;
    }

    this.drawer.addEventListener('click', this._handleDrawerClick);

    // Initialize tooltips for size selectors
    elabs.Bootstrap.initialiseTooltips(`${SELECTORS.DRAWER_INNER} [data-tippy-content]`);

    // Load product data for size selectors
    this._initializeSizeSelectors();
  };

  /**
   * Handle click events in the drawer
   */
  _handleDrawerClick = async (event) => {
    const removeButton = event.target.closest(SELECTORS.REMOVE_BUTTON);
    const moveToCartButton = event.target.closest(SELECTORS.MOVE_TO_CART_BUTTON);
    const sizeOptionButton = event.target.closest(SELECTORS.SIZE_OPTION_BUTTON);
    const clearAllButton = event.target.closest(SELECTORS.CLEAR_ALL_BUTTON);

    if (removeButton) {
      event.preventDefault();
      await this._handleRemove(removeButton);
    }

    if (moveToCartButton) {
      event.preventDefault();
      await this._handleMoveToCart(moveToCartButton);
    }

    if (sizeOptionButton) {
      event.preventDefault();
      await this._handleSizeSelection(sizeOptionButton);
    }

    if (clearAllButton) {
      event.preventDefault();
      await this._handleClearAll();
    }
  };

  /**
   * Initialize size selectors with product data
   */
  _initializeSizeSelectors = async () => {
    const sizeSelectors = this.drawer?.querySelectorAll(SELECTORS.SIZE_SELECTOR);
    if (!sizeSelectors?.length) {
      return;
    }

    for (const selector of sizeSelectors) {
      const productId = selector.dataset.productId;
      const productUrl = selector.dataset.productUrl;
      if (!productId) {
        continue;
      }

      try {
        const productData = await this._fetchProductData(productId, productUrl);
        if (productData) {
          this._updateSizeSelectorTooltip(selector, productData);
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    }
  };

  /**
   * Fetch product data from Shopify
   */
  _fetchProductData = async (productId, productUrl = null) => {
    // Check cache first
    if (this.productDataCache.has(productId)) {
      return this.productDataCache.get(productId);
    }

    // Try to get URL from parameter or find from wishlist items
    let url = productUrl;
    if (!url) {
      const item = this.wishlistItems.find((i) => String(i.ProductId) === String(productId));
      url = item?.ProductLink;
    }

    if (!url) {
      console.warn('No product URL found for productId:', productId);
      return null;
    }

    // Extract handle from URL
    const handleMatch = url.match(/\/products\/([^/?]+)/);
    if (!handleMatch) {
      return null;
    }

    const handle = handleMatch[1];

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      const productData = await response.json();
      this.productDataCache.set(productId, productData);
      return productData;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  };

  /**
   * Update prices and availability from Shopify API
   */
  _updatePricesFromShopify = async () => {
    const items = this.drawer?.querySelectorAll('.el-wishlist-drawer__item');
    if (!items?.length) {
      return;
    }

    for (const itemEl of items) {
      const productId = itemEl.dataset.productId;
      const variantId = itemEl.dataset.variantId;
      const hasNoVariant = itemEl.dataset.hasNoVariant === 'true';
      const productUrl = itemEl.querySelector('.el-wishlist-drawer__item-image-wrapper')?.href;

      if (!productId) {
        continue;
      }

      try {
        const productData = await this._fetchProductData(productId, productUrl);
        if (!productData) {
          continue;
        }

        // Find the specific variant or use first variant
        const variant = productData.variants.find((v) => String(v.id) === String(variantId)) || productData.variants[0];

        if (variant) {
          const priceEl = itemEl.querySelector('.el-wishlist-drawer__item-price .el-price__price');
          const compareAtPriceEl = itemEl.querySelector('.el-wishlist-drawer__item-price .el-price__compare-at-price');

          if (priceEl) {
            // Format price with current currency
            const formattedPrice = this._formatShopifyPrice(variant.price);
            priceEl.textContent = formattedPrice;
          }

          // Show compare_at_price if exists and is higher than price
          if (compareAtPriceEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              const formattedComparePrice = this._formatShopifyPrice(variant.compare_at_price);
              compareAtPriceEl.innerHTML = `<s>${formattedComparePrice}</s>`;
              compareAtPriceEl.style.display = '';
            } else {
              compareAtPriceEl.innerHTML = '';
              compareAtPriceEl.style.display = 'none';
            }
          }

          // Update size display (only if product has multiple variants and variant is selected)
          const hasMultipleVariants = productData.variants.length > 1;
          const sizeWrapper = itemEl.querySelector('.el-wishlist-drawer__item-size-wrapper');
          if (sizeWrapper && hasMultipleVariants && !hasNoVariant) {
            const variantSize = variant.option1 || variant.title || '';
            if (variantSize) {
              sizeWrapper.innerHTML = `<div class="el-wishlist-drawer__item-size">${variantSize}</div>`;
            }
          }

          // Update button based on availability
          if (!hasNoVariant) {
            // For items with selected variant - check that variant's availability
            this._updateMoveToCartButton(itemEl, !variant.available);
          } else {
            // For items without selected variant - check if ALL variants are sold out
            const allSoldOut = productData.variants.every((v) => !v.available);
            if (allSoldOut) {
              this._updateMoveToCartButton(itemEl, true);
            }
          }
        }
      } catch (error) {
        console.error('Error updating price:', error);
      }
    }
  };

  /**
   * Update Move to Cart button based on sold out status
   */
  _updateMoveToCartButton(itemEl, isSoldOut) {
    const moveToCartBtn = itemEl.querySelector(SELECTORS.MOVE_TO_CART_BUTTON);
    if (!moveToCartBtn) {
      return;
    }

    const btnTextEl = moveToCartBtn.querySelector('.el-wishlist-drawer__item-move-to-cart--text');
    if (isSoldOut) {
      moveToCartBtn.dataset.soldOut = 'true';
      itemEl.dataset.soldOut = 'true';
      if (btnTextEl) {
        btnTextEl.textContent = 'Create Stock Alert';
      }
    } else {
      moveToCartBtn.removeAttribute('data-sold-out');
      itemEl.removeAttribute('data-sold-out');
      if (btnTextEl) {
        btnTextEl.textContent = 'Move to Cart';
      }
    }
  }

  /**
   * Format Shopify price (in cents) with current currency using Intl.NumberFormat
   */
  _formatShopifyPrice(priceInCents) {
    const price = priceInCents / 100;
    const currencyCode = window.Shopify?.currency?.active || 'GBP';
    const locale = this._getLocaleForCurrency(currencyCode);

    try {
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: price % 1 === 0 ? 0 : 2,
      });
      return formatter.format(price);
    } catch {
      // Fallback if Intl fails
      const symbol = this._getCurrencySymbol(currencyCode);
      const formatted = price % 1 === 0 ? price.toFixed(0) : price.toFixed(2);
      return `${symbol}${formatted}`;
    }
  }

  /**
   * Get locale for currency code
   */
  _getLocaleForCurrency(currencyCode) {
    const locales = {
      GBP: 'en-GB',
      USD: 'en-US',
      EUR: 'de-DE',
      CAD: 'en-CA',
      AUD: 'en-AU',
    };
    return locales[currencyCode] || 'en-US';
  }

  /**
   * Update size selector tooltip with actual sizes (including sold out)
   */
  _updateSizeSelectorTooltip(selector, productData) {
    const tippyInstance = selector._tippy;
    if (!tippyInstance) {
      return;
    }

    const variants = productData.variants || [];

    if (variants.length === 0) {
      tippyInstance.setContent(`
        <div class="el-wishlist-drawer__item-size-selector--tooltip">
          <div class="el-wishlist-drawer__size-unavailable">No sizes available</div>
        </div>
      `);
      return;
    }

    const baseProductName = productData.title || '';
    const optionsHTML = variants
      .map((variant) => {
        const variantTitle = variant.option1 || variant.title || '';
        const isSoldOut = !variant.available;
        return `
      <button type="button" 
              class="el-wishlist-drawer__item-size-selector--tooltip-option"
              data-variant-id="${variant.id}"
              data-variant-title="${variant.title}"
              data-product-id="${productData.id}"
              data-product-name="${baseProductName}"
              data-sold-out="${isSoldOut}">
        ${variantTitle}
      </button>
    `;
      })
      .join('');

    tippyInstance.setContent(`
      <div class="el-wishlist-drawer__item-size-selector--tooltip">
        ${optionsHTML}
      </div>
    `);
  }

  /**
   * Handle size selection
   */
  _handleSizeSelection = async (button) => {
    const newVariantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const variantTitle = button.dataset.variantTitle;
    const productName = button.dataset.productName; // Clean product name (without variant)
    const isSoldOut = button.dataset.soldOut === 'true';
    const itemElement = button.closest(SELECTORS.ITEM);

    if (!itemElement || !newVariantId) {
      return;
    }

    const oldVariantId = itemElement.dataset.variantId;

    this._toggleLoading(true, itemElement);

    try {
      // Get the original item data (compare as strings since dataset values are strings)
      const originalItem = this.wishlistItems.find((item) => String(item.ProductVariantId) === String(oldVariantId));

      if (!originalItem) {
        throw new Error('Original item not found');
      }

      let wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      // Delete the old item (with no variant marker)
      await window.WishListHero_SDK.DeleteWishListItem({
        ProductVariantId: parseInt(oldVariantId),
        WishlistHash: wishlistHash,
      });

      // Wait for SDK to process delete
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Get fresh hash after delete
      wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      // Add the new item with specific variant
      // Handle URL - either replace existing variant param or add new one
      let newProductLink = originalItem.ProductLink;
      if (newProductLink.includes('variant=')) {
        newProductLink = newProductLink.replace(/variant=\d+/, `variant=${newVariantId}`);
      } else {
        const separator = newProductLink.includes('?') ? '&' : '?';
        newProductLink = `${newProductLink}${separator}variant=${newVariantId}`;
      }
      // Use productName from button (includes variant title) or fallback to clean name
      const newProductTitle = productName || this._getCleanProductName(originalItem);
      // SDK returns Price after reload, ProductPrice immediately after add
      // Use nullish coalescing to handle 0 values correctly
      const itemPrice = originalItem.Price ?? originalItem.ProductPrice ?? originalItem.productPrice ?? 0;

      const addPayload = {
        ProductId: parseInt(productId),
        ProductTitle: newProductTitle,
        ProductPrice: itemPrice,
        ProductLink: newProductLink,
        ProductVariantId: parseInt(newVariantId),
        ProductImage: originalItem.ProductImage,
        WishlistHash: wishlistHash,
      };

      await window.WishListHero_SDK.AddWishListItem(addPayload);

      // Note: No tracking for variant swap — selecting a size is not a real add/remove action

      // Wait for SDK to process add
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Force SDK to refresh its internal state
      if (window.WishListHero_SDK.RefreshWishList) {
        await window.WishListHero_SDK.RefreshWishList();
      }

      // Update the UI
      itemElement.dataset.variantId = newVariantId;
      itemElement.dataset.hasNoVariant = 'false';

      // Replace size selector with size display
      const sizeWrapper = itemElement.querySelector('.el-wishlist-drawer__item-size-wrapper');
      if (sizeWrapper) {
        sizeWrapper.innerHTML = `<div class="el-wishlist-drawer__item-size">${variantTitle}</div>`;
      }

      // Update move to cart button based on availability
      const moveToCartBtn = itemElement.querySelector(SELECTORS.MOVE_TO_CART_BUTTON);
      if (moveToCartBtn) {
        moveToCartBtn.removeAttribute('data-needs-variant');
      }
      this._updateMoveToCartButton(itemElement, isSoldOut);

      // Hide the tippy
      const sizeSelector = itemElement.querySelector(SELECTORS.SIZE_SELECTOR);
      if (sizeSelector?._tippy) {
        sizeSelector._tippy.hide();
      }

      // Refresh wishlist items
      await this._loadWishlistItems();

      // Dispatch update event (will re-render drawer and call _updatePricesFromShopify which handles availability)
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    } catch (error) {
      console.error('Error updating variant:', error);
    } finally {
      this._toggleLoading(false, itemElement);
    }
  };

  /**
   * Handle remove button click
   */
  _handleRemove = async (button) => {
    const itemElement = button.closest(SELECTORS.ITEM);
    if (!itemElement) {
      return;
    }

    const variantId = itemElement.dataset.variantId;

    // Capture item data before removal for tracking
    const itemForTracking = this.wishlistItems.find((i) => String(i.ProductVariantId) === String(variantId));

    this._toggleLoading(true, itemElement);

    try {
      const wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      await window.WishListHero_SDK.DeleteWishListItem({
        ProductVariantId: parseInt(variantId),
        WishlistHash: wishlistHash,
      });

      // Track removal event (fire-and-forget)
      if (itemForTracking) {
        trackRemovedFromWishlist({
          productId: itemForTracking.ProductId,
          variantId: itemForTracking.ProductVariantId,
          name: this._getCleanProductName(itemForTracking),
          price: itemForTracking.Price || itemForTracking.ProductPrice || 0,
          url: itemForTracking.ProductLink || '',
          image: itemForTracking.ProductImage || '',
        });
      }

      // Remove item from DOM with animation
      itemElement.style.opacity = '0';
      itemElement.style.transform = 'translateX(20px)';

      setTimeout(() => {
        itemElement.remove();
        this._updateEmptyState();
      }, 200);

      // Refresh wishlist items
      await this._loadWishlistItems();

      // Dispatch update event
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    } catch (error) {
      console.error('Error removing item:', error);
      this._toggleLoading(false, itemElement);
    }
  };

  /**
   * Handle move to cart button click
   */
  _handleMoveToCart = async (button) => {
    const itemElement = button.closest(SELECTORS.ITEM);
    if (!itemElement) {
      return;
    }

    const needsVariant = button.dataset.needsVariant === 'true';
    const isSoldOut = button.dataset.soldOut === 'true' || itemElement.dataset.soldOut === 'true';
    const variantId = itemElement.dataset.variantId;
    const hasNoVariant = itemElement.dataset.hasNoVariant === 'true';

    // If sold out, redirect to PDP for stock alert
    if (isSoldOut) {
      const productLink = itemElement.querySelector('.el-wishlist-drawer__item-title');
      if (productLink?.href) {
        window.location.href = productLink.href;
      }
      return;
    }

    // If no variant selected, show size selector tooltip or redirect to PDP
    if (needsVariant || hasNoVariant) {
      const sizeSelector = itemElement.querySelector(SELECTORS.SIZE_SELECTOR);
      const tippyInstance = sizeSelector?._tippy;

      // Check if size selector exists and has available sizes
      if (tippyInstance) {
        const tooltipContent = tippyInstance.props.content;
        const hasAvailableSizes =
          tooltipContent && !tooltipContent.includes('No sizes available') && !tooltipContent.includes('Loading');

        if (hasAvailableSizes) {
          // Show the size selector tooltip
          tippyInstance.show();
          return;
        }
      }

      // No sizes available - redirect to PDP
      const productLink = itemElement.querySelector('.el-wishlist-drawer__item-title');
      if (productLink?.href) {
        window.location.href = productLink.href;
      }
      return;
    }

    // Capture item data before removal for tracking
    const itemForTracking = this.wishlistItems.find((i) => String(i.ProductVariantId) === String(variantId));

    this._toggleLoading(true, itemElement);

    try {
      // Add to cart with sections for cart drawer
      const sectionsToRender = ['el-cart-drawer-inner', 'cart-icon-bubble'];
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cartAddResponse = await response.json();

      // Remove from wishlist
      const wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      await window.WishListHero_SDK.DeleteWishListItem({
        ProductVariantId: parseInt(variantId),
        WishlistHash: wishlistHash,
      });

      // Track removal event (fire-and-forget)
      if (itemForTracking) {
        trackRemovedFromWishlist({
          productId: itemForTracking.ProductId,
          variantId: itemForTracking.ProductVariantId,
          name: this._getCleanProductName(itemForTracking),
          price: itemForTracking.Price || itemForTracking.ProductPrice || 0,
          url: itemForTracking.ProductLink || '',
          image: itemForTracking.ProductImage || '',
        });
      }

      // Remove item from DOM with animation
      itemElement.style.opacity = '0';
      itemElement.style.transform = 'translateX(20px)';

      setTimeout(() => {
        itemElement.remove();
        this._updateEmptyState();
      }, 200);

      // Refresh wishlist items
      await this._loadWishlistItems();

      // Dispatch events
      document.dispatchEvent(new CustomEvent('wishlist:updated'));

      // Close wishlist drawer first
      elabs.overlayMenuService.closeMenu();

      // Dispatch cart update with sections from add response
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { source: 'wishlist-drawer', cart: cartAddResponse },
          }),
        );
      }, 350);
    } catch (error) {
      console.error('Error moving to cart:', error);
      this._toggleLoading(false, itemElement);
    }
  };

  /**
   * Handle clear all wishlist items
   */
  _handleClearAll = async () => {
    if (!window.WishListHero_SDK || this.wishlistItems.length === 0) {
      return;
    }

    try {
      const wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      // Delete all items one by one
      for (const item of this.wishlistItems) {
        await window.WishListHero_SDK.DeleteWishListItem({
          ProductVariantId: parseInt(item.ProductVariantId),
          WishlistHash: wishlistHash,
        });
      }

      // Force SDK to refresh its internal state
      if (window.WishListHero_SDK.RefreshWishList) {
        await window.WishListHero_SDK.RefreshWishList();
      }

      // Clear local items
      this.wishlistItems = [];

      // Track cleared event (fire-and-forget)
      trackClearedWishlist();

      // Update UI
      this._refreshDrawerContent();

      // Dispatch update event
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    } catch (error) {
      console.error('Error clearing wishlist:', error);
    }
  };

  /**
   * Update empty state visibility
   */
  _updateEmptyState() {
    if (!this.drawer) {
      return;
    }

    const itemsContainer = this.drawer.querySelector(SELECTORS.ITEMS_CONTAINER);
    const emptyState = this.drawer.querySelector(SELECTORS.EMPTY_STATE);
    const withItemsContainer = this.drawer.querySelector('.el-wishlist-drawer__with-items');

    const remainingItems = itemsContainer?.querySelectorAll(SELECTORS.ITEM)?.length || 0;

    if (remainingItems === 0) {
      emptyState?.classList.remove('hidden');
      withItemsContainer?.classList.add('hidden');
    }
  }

  /**
   * Toggle loading state for an item
   */
  _toggleLoading(isLoading, itemElement) {
    if (!itemElement) {
      return;
    }

    itemElement.classList.toggle('loading', isLoading);

    const loaderEl = itemElement.querySelector('.el-wishlist-drawer__loader-spinner-wrapper');
    if (loaderEl) {
      loaderEl.classList.toggle('hidden', !isLoading);
    }
  }

  /**
   * Refresh drawer content
   */
  _refreshDrawerContent = async () => {
    if (!this.drawer) {
      return;
    }

    await this._loadWishlistItems();

    const itemsContainer = this.drawer.querySelector(SELECTORS.ITEMS_CONTAINER);
    if (itemsContainer) {
      itemsContainer.innerHTML = this._generateItemsHTML();
      elabs.Bootstrap.initialiseTooltips(`${SELECTORS.DRAWER_INNER} [data-tippy-content]`);
      this._initializeSizeSelectors();
      // Update prices from Shopify API after refresh
      this._updatePricesFromShopify();
    }

    this._updateEmptyState();
  };

  /**
   * Get current wishlist count from SDK
   */
  async getWishlistCount() {
    try {
      const items = await window.WishListHero_SDK.GetWishListItems();
      return items?.length || 0;
    } catch {
      return this.wishlistItems.length;
    }
  }

  /**
   * Check if a variant is in the wishlist
   * @param {number|string} variantId - The variant ID to check
   * @returns {boolean}
   */
  isVariantInWishlist(variantId) {
    if (!variantId || !this.wishlistItems.length) {
      return false;
    }
    return this.wishlistItems.some((item) => String(item.ProductVariantId) === String(variantId));
  }

  /**
   * Get wishlist items (for external use)
   */
  getWishlistItems() {
    return this.wishlistItems;
  }

  /**
   * Ensure wishlist items are loaded
   */
  ensureItemsLoaded = async () => {
    if (this.wishlistItems.length === 0 && window.WishListHero_SDK) {
      await this._loadWishlistItems();
    }
    return this.wishlistItems;
  };

  /**
   * Add item to wishlist (helper method for external use)
   * @param {Object} productInfo - Product information
   * @param {boolean} hasVariant - Whether a specific variant was selected
   * @param {boolean} openDrawer - Whether to open the wishlist drawer after adding
   */
  addToWishlist = async (productInfo, hasVariant = true, openDrawer = false) => {
    if (!window.WishListHero_SDK) {
      console.error('Wishlist Hero SDK not available');
      return false;
    }

    // Show toast notification optimistically (don't wait for network)
    const heartIcon = window.elabsAssets?.wishlistIconOutlineWhite || '';
    const cleanTitle = productInfo.title?.replace(/\s{3}$/, '') || 'Item';
    showToast(`${cleanTitle} has been added to your wishlist`, heartIcon);

    try {
      const wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      // Add marker if no variant selected
      const productTitle = hasVariant ? productInfo.title : productInfo.title + NO_VARIANT_MARKER;

      await window.WishListHero_SDK.AddWishListItem({
        ProductId: parseInt(productInfo.productId),
        ProductTitle: productTitle,
        ProductPrice: parseFloat(productInfo.price),
        ProductLink: productInfo.url,
        ProductVariantId: parseInt(productInfo.variantId),
        ProductImage: productInfo.image,
        WishlistHash: wishlistHash,
      });

      // Force SDK to refresh its internal state
      if (window.WishListHero_SDK.RefreshWishList) {
        await window.WishListHero_SDK.RefreshWishList();
      }

      // Wait for SDK to update its internal state
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refresh items from SDK
      await this._loadWishlistItems();

      // Track added event (fire-and-forget)
      trackAddedToWishlist({
        productId: productInfo.productId,
        variantId: productInfo.variantId,
        name: productInfo.title,
        price: productInfo.price,
        url: productInfo.url,
        image: productInfo.image,
      });

      // Dispatch update event
      document.dispatchEvent(new CustomEvent('wishlist:updated'));

      // Open drawer if requested
      if (openDrawer) {
        // Close any open overlay first
        if (document.body.classList.contains('el-overlay-menu-open')) {
          elabs.overlayMenuService.closeMenu();
          // Wait for close animation to complete
          await new Promise((resolve) => setTimeout(resolve, 350));
        }
        await this.openDrawer();
      }

      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      showToast('Could not update wishlist. Please try again.', heartIcon);
      return false;
    }
  };

  /**
   * Remove item from wishlist (helper method for external use)
   * @param {string|number} variantId - Variant ID to remove
   * @param {string} productTitle - Optional product title for toast message
   */
  removeFromWishlist = async (variantId, productTitle = null) => {
    if (!window.WishListHero_SDK) {
      console.error('Wishlist Hero SDK not available');
      return false;
    }

    // Capture item data before removal for tracking
    const itemForTracking = this.wishlistItems.find((i) => String(i.ProductVariantId) === String(variantId));

    // Get product title before removing if not provided
    let title = productTitle;
    if (!title) {
      title = itemForTracking?.ProductName || 'Item';
    }

    // Show toast notification optimistically (don't wait for network)
    const heartIcon = window.elabsAssets?.wishlistIconOutlineWhite || '';
    const cleanTitle = title?.replace(/\s{3}$/, '') || 'Item';
    showToast(`${cleanTitle} has been removed from your wishlist`, heartIcon);

    try {
      const wishlistHash = await window.WishListHero_SDK.GetWishListHash(true);

      await window.WishListHero_SDK.DeleteWishListItem({
        ProductVariantId: parseInt(variantId),
        WishlistHash: wishlistHash,
      });

      // Force SDK to refresh its internal state
      if (window.WishListHero_SDK.RefreshWishList) {
        await window.WishListHero_SDK.RefreshWishList();
      }

      // Refresh items
      await this._loadWishlistItems();

      // Track removal event (fire-and-forget)
      if (itemForTracking) {
        trackRemovedFromWishlist({
          productId: itemForTracking.ProductId,
          variantId: itemForTracking.ProductVariantId,
          name: this._getCleanProductName(itemForTracking),
          price: itemForTracking.Price || itemForTracking.ProductPrice || 0,
          url: itemForTracking.ProductLink || '',
          image: itemForTracking.ProductImage || '',
        });
      }

      // Dispatch update event
      document.dispatchEvent(new CustomEvent('wishlist:updated'));

      return true;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showToast('Could not update wishlist. Please try again.', heartIcon);
      return false;
    }
  };

  /**
   * Check if variant is in wishlist
   */
  isInWishlist(variantId) {
    return this.wishlistItems.some((item) => item.ProductVariantId === String(variantId));
  }
};

// Initialize when ready
elabs.onJavascriptReady(() => {
  elabs.wishlistDrawer = new elabs.WishlistDrawer();
});
